import type {
  HasSessionFailureResponse,
  HasSessionSuccessResponse,
} from "@schibsted/account-sdk-browser";

import { getIdentityInstance } from "./getIdentityInstance";

const isError = (
  response: HasSessionSuccessResponse | HasSessionFailureResponse,
): response is HasSessionFailureResponse =>
  (response as HasSessionFailureResponse).error !== undefined;

const sessionObj = { sig: "", userId: "" };

// Identity session cache with metadata for hybrid invalidation strategy
interface IdentitySessionCache {
  value: { sig: string; userId: string };
  isLoggedIn: boolean; // Track user login state
  pathname: string;
  timestamp: number;
}

let identitySessionCache: IdentitySessionCache | null = null;
let identitySessionPromise: Promise<{
  sig: string;
  userId: string;
}> | null = null;

// Cache TTL: 5 minutes (session rarely changes within page view)
const IDENTITY_SESSION_CACHE_TTL = 5 * 60 * 1000;

/**
 * Get identity session with hybrid caching strategy:
 * - Caches result per pathname (invalidates on navigation)
 * - Invalidates when login state changes
 * - Expires after TTL (prevents stale data)
 * - Supports manual invalidation
 */
const getIdentitySession = async (
  isLoggedIn: boolean,
): Promise<{ sig: string; userId: string }> => {
  const currentPathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const now = Date.now();

  // Invalidate cache if:
  // 1. Different pathname (user navigated)
  // 2. Login state changed (logged in/out)
  // 3. Cache expired (TTL exceeded)
  // 4. No cache exists
  if (
    !identitySessionCache ||
    identitySessionCache.pathname !== currentPathname ||
    identitySessionCache.isLoggedIn !== isLoggedIn ||
    now - identitySessionCache.timestamp > IDENTITY_SESSION_CACHE_TTL
  ) {
    identitySessionCache = null;
    identitySessionPromise = null;
  }

  // Return cached value if valid
  if (identitySessionCache) {
    return identitySessionCache.value;
  }

  // Return pending promise if fetch is in progress (prevent duplicate fetches)
  if (identitySessionPromise) {
    return identitySessionPromise;
  }

  // If not logged in, return empty session immediately (no fetch needed)
  if (!isLoggedIn) {
    const emptySession = { sig: "", userId: "" };
    identitySessionCache = {
      value: emptySession,
      isLoggedIn,
      pathname: currentPathname,
      timestamp: now,
    };
    return emptySession;
  }

  // Fetch identity session
  identitySessionPromise = (async () => {
    try {
      const identity = getIdentityInstance();
      const session = await identity.hasSession();

      if (isError(session)) {
        throw new Error(
          `Error of type ${session.error.type} with description ${session.error.description} when getting sig`,
        );
      }

      const sessionData = {
        sig: session.sig,
        userId: session.userId.toString(),
      };

      // Cache with metadata
      identitySessionCache = {
        value: sessionData,
        isLoggedIn,
        pathname: currentPathname,
        timestamp: now,
      };
      identitySessionPromise = null;

      return sessionData;
    } catch (e) {
      console.error("getIdentitySession: Failed to get session", e);
      identitySessionPromise = null;
      // Return empty session on error
      return sessionObj;
    }
  })();

  return identitySessionPromise;
};

/**
 * Manually invalidate identity session cache
 * Call this if user logs in/out or session changes
 */
const invalidateIdentitySessionCache = () => {
  identitySessionCache = null;
  identitySessionPromise = null;
};

export { isError, getIdentitySession, invalidateIdentitySessionCache };
